/*
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

Author                    Amar K

Created By                

Created On         

Description               Member List Page Component

Last Updated on           16-10-2023 

Last Updated By           Amar K

Last Update Description   Data Driven Registry Implementation


/////////////////////////////////////////////////
Additional Information
/////////////////////////////////////////////////

* If Label changes are done for ADT & ER then for the same when getMembers api is done mapping for the patient id list should be changed.

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
*/

import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import classNames from "classnames";
import Highlighter from "react-highlight-words";
import { useNavigate } from "react-router-dom";
import { cloneDeep, map, get, isEmpty , uniqBy , isFunction, find } from "lodash";
import { Radio, DatePicker, Select, Table, Drawer, Button, Row, Col, Tooltip, Badge, Pagination, Dropdown, Checkbox, Menu, Space, Spin, Popover } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { forkJoin } from "rxjs";
import { CanEdit } from "@components/accessLevel";
// include local dependencies
import Filters from "@components/filters";
import Layout from "@layout/default";
import TableConfig from "@components/tableConfigDrawer";
import { appInsights, reactPlugin, withAITracking } from "../../appInsights";
import { getProgressColorCode } from "@pages/utils/utils";
import TableCard from "@components/tableCard";
import { TMembersProps, layerViews, IMembers } from "./constants";
import { ASC, DESC } from "@utils/constants";
import DownloadSection from "@components/downloadSection";
import KeyInsight from "./components/keyInsights";
import { TFilterOption } from "@components/filters/constant";
import IconChevron from "@assets/icon_chevron.svg";
import memberService from "@services/members.service";
import membersService from "@services/members.service";
import { MEMBERS_SCREEN_TITLE, routeMap, dateFormatConfig, HOSPITAL_LIST_AND_CM_USER_OPENED_FROM } from "@utils/constants";
import { useMemberListStore } from "@stores/useMemberListStore";
import sessionService from "@services/session.service";
import { centricClientCheck, fixedDecimal, formatDisplayDate, getClientName, getColumn } from "@utils/helpers";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import Icon from "@assets/icon_config";
import TableActions from "./components/tableActions";
import CheckBox from "./components/checkbox";
import { useHospitalMappingStore } from "../../stores/useHospitalMappingStore";
import { useGlobalInformationStore } from "../../stores/global_informationStore";
import "./members.scss";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import RefreshData from "@components/refreshData/refreshData";
import { ColFilters, FilterCountBtn, ClearFilters, FilterColumnRemoval, useColumnFilters, FilterIcon, SortHeader } from "@components/filters/colFilters";
import TablePreloader from "@components/preloaders/Table/tablePreLoader";
import { useDurableStore } from "@stores/useDurableStore";
import memberDetailsService from "@services/memberDetails.service";
import { useDropdowns } from "@utils/customHooks";
import { TableLocale } from "@components/noDataFound";
import storage from "@services/storage.service";
import { useConfigStore } from "@stores/useConfigStore";
import { useMeasuresStore } from "@stores/measuresStore";
import { useChronicBreakdownStore } from "@stores/chronicBreakdownStore";
// import Admission from "./admission.json"
// import CaseLoad from "./caseLoad.json"
/********************************************** */

const { Option } = Select;
const { RangePicker } = DatePicker;

const rowsPerPageArray: string[] = ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50"];

let pageName = 'Member List';
const expandableColumns: string[] = ["risk_category"]; //"patient_case_status"
const unSortableColumns : string[] = ["action", "member_caseload_status", "patient_eol_status"]

const Members: FC<TMembersProps> = () => {
  const setCurrentScreen = useGlobalInformationStore((state) => state.setCurrentScreen);
  const setNextFunc = useGlobalInformationStore((state) => state.setNextFunc);
  
  const pIds = usePopulationInsightStore((state) => state.patientIds);
  const measuresPatientIds = useMeasuresStore((state) => state.patientIds)
  const chronicPatientIds = useChronicBreakdownStore((state) => state.patientIds)
  const comingFromPopulationInst = usePopulationInsightStore((state) => {
    return state.comingFromPopulationInst;
  });
  const comingFromMeasures = useMeasuresStore((state) => {
    return state.comingFromMeasures;
  });
  const comingFromChronicBreakdown = useChronicBreakdownStore((state) =>  state.comingFromChronicBreakdown);
  const chronicName = useChronicBreakdownStore((state) => state.chronicName);
  const measureName = useMeasuresStore((state) => state.measureName);
  const selectedYearInMeasuresReport = useMeasuresStore((state) => state.selectedYear);
  const handleComingFromMembersFlag = usePopulationInsightStore((state) => state.handleComingFromMembersFlag);

  const _stateInfo = usePopulationInsightStore((state) => state.stateInfo);
  const _activeSideCardIndex = usePopulationInsightStore((state) => state.activeSideCardIndex);
  const _report = get(_stateInfo, "sideCardData", [])?.[_activeSideCardIndex ? _activeSideCardIndex : 0];
  const data = useMemberListStore((state) => state.data);
  const setMemberListData = useMemberListStore((state) => state.setMemberListData);
  const rowsPerPage = useMemberListStore((state) => state.rowsPerPage);
  const setRowsPerPage = useMemberListStore((state) => state.setRowsPerPage);
  const totalPages = useMemberListStore((state) => state.totalPages);
  const setTotalPages = useMemberListStore((state) => state.setTotalPages);
  const registryDependentMemberListPageStore = useMemberListStore((state) => state.registryDependentMemberListPageStore);
  const setRegistryDependentMemberListPageStore = useMemberListStore((state) => state.setRegistryDependentMemberListPageStore);
  const [noData, setNoData] = useState<boolean | undefined>(false);
  const reminderStartFilter = useMemberListStore((state) => state.reminderStartFilter);
  const setReminderStartFilter = useMemberListStore((state) => state.setReminderStartFilter);
  const reminderEndFilter = useMemberListStore((state) => state.reminderEndFilter);
  const setReminderEndFilter = useMemberListStore((state) => state.setReminderEndFilter);
  const sortType = useMemberListStore((state) => state.sortType);
  const setSortTypeFilter = useMemberListStore((state) => state.setSortTypeFilter);
  const sortBy = useMemberListStore((state) => state.sortBy);
  const setSortByFilter = useMemberListStore((state) => state.setSortByFilter);
  const [loading, setLoaderFlag] = useState(false);
  const [columnListdata, setColumnListData] = useState<any>([]);
  const [downloadColData, setDownloadColData] = useState<any>([]);
  const [masterData, setMasterData] = useState(null);
  const adtState = useMemberListStore((state) => state.adtState);
  const setADTState = useMemberListStore((state) => state.setADTState);
  const columnSearchInfo = useMemberListStore((state) => state.columnSearchInfo);
  // const setColumnSearchInfo = useMemberListStore((state) => state.setColumnSearchInfo);
  const defaultAdmitAndDischargeStartDate = useMemberListStore((state) => state.defaultAdmitAndDischargeStartDate);
  // const setDefaultAdmitAndDischargeStartDate = useMemberListStore((state) => state.setDefaultAdmitAndDischargeStartDate);
  const defaultAdmitAndDischargeEndDate = useMemberListStore((state) => state.defaultAdmitAndDischargeEndDate);
  // const patientIdList = useMemberListStore((state) => state.patientIdList);
  const setPatientIdList = useMemberListStore((state) => state.setPatientIdList);
  const [isloadingCaseStatus, setIsloadingCaseStatus] = useState(true);

  const ADTPatientIdList = useMemberListStore((state) => state.ADTPatientIdList);

  const [caseLoadCount] = useState(0);
  const setCaseLoadLoader = useMemberListStore((state) => state.setCaseLoadLoader);
  const [rowsPerPageToggle, setRowsPerPageToggle] = useState<boolean>(false);
  const filteredInfo = useMemberListStore((state) => state.filteredInfo);
  const setFilteredInfo = useMemberListStore((state) => state.setFilteredInfo);
  const setShowMemberDetailsModal = useHospitalMappingStore((state) => state.setShowMemberDetailsModal);
  const setSelectedHospitalListFacility = useGlobalInformationStore((state) => state.setSelectedHospitalListFacility);
  const setHospitalMappingEditModeOpenedFrom = useHospitalMappingStore((state) => state.setHospitalMappingEditModeOpenedFrom);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const filterDropdowns = useDurableStore((state) => state.filterDropdowns);
  const showFilters = useMemberListStore((state) => state.showFilters);
  const setShowFilters = useMemberListStore((state) => state.setShowFilters);
  const [isFilterModal, setIsFilterModal] = useState<{ open: boolean; action: string; data: any; onOk?: any; colNames?: Array<string> }>({ open: false, action: "", data: {}, colNames: [] });
  const [tableConfigData, setTableConfigData] = useState<any>();
  const { colFilterMiddleWare, filterCount, getDropdownFilters } = useColumnFilters();
  const [chronicPopoverOpen, setChronicPopoverOpen] = useState<boolean>(false);
  const [dataRefreshStatus, setDataRefreshStatus] = useState<any>(null);
  const [masterDataForAdmitAndDischarge, setMasterDataForAdmitAndDischarge] = useState<any>({});

  const memberListRegistryDropDown = useMemberListStore((state) => state.memberListRegistryDropDown);
  const setMemberListRegistryDropDown = useMemberListStore((state) => state.setMemberListRegistryDropDown);
  const [notificationsData, setNotificationsData] = useState<any>(null);
  const config: any = useConfigStore((state) => state.config);
  const clientName = getClientName();
  let registryDropDownAndTableConfig = memberListRegistryDropDown;

  const getMemberListRegistryDropDown = () => {
    setLoaderFlag(true);
    memberDetailsService.fetchAttributeDropdown("MemlistRegistryLst").subscribe({
      next: (response: any) => {
        const data = response?.data?.data?.data?.map((item: any) => {
          let tempItem: any = cloneDeep(item);
          tempItem = {
            ...tempItem,
            ...(tempItem?.defaultProperties ? { defaultProperties: JSON.parse(tempItem?.defaultProperties) } : {}),
          };
          return tempItem;
        });
        setMemberListRegistryDropDown(data);
        registryDropDownAndTableConfig = data;
        initialDataLoad();
      },
      error: (error: any) => {
        setLoaderFlag(false);
        console.log(error);
      },
    });
  };

  const {getOptions} = useDropdowns();

  const navigate = useNavigate();
  const isCentric = centricClientCheck()

  const handleReminderDateFilter = (range: any) => {
    const reminderStartDate = range && range.length > 0 ? range[0].format("YYYY-MM-DD") : undefined;
    const reminderEndDate = range && range.length > 0 ? range[1].format("YYYY-MM-DD") : undefined;
    setReminderStartFilter(reminderStartDate);
    setReminderEndFilter(reminderEndDate);

    getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartDate,
      reminderEndDate: reminderEndDate,
      adtFlag: adtState,
      filterBody: filteredInfo,
    });
  };

  const goToMemberDetails = (record: IMembers) => {
    membersService.setCurrentMember = record;
    navigate(routeMap.memberProfile);
  };

  const initialDataLoad = () => {
    getMemberListDataRefreshStatus();
    console.log("Members.useEffect2");
    setCurrentScreen(MEMBERS_SCREEN_TITLE);
    appInsights.setAuthenticatedUserContext(sessionService?.getUser()?.email);
    appInsights.trackPageView({ name: "Members" });

    // Update Member list registry dropdown and adtState
    const ADTregistrySelected = comingFromPopulationInst || comingFromMeasures || comingFromChronicBreakdown
      ? find(registryDropDownAndTableConfig, { label: "Filtered Members" })?.label
      : registryDropDownAndTableConfig?.filter((item: any) => {
          if (selectedMemberType) {
            return selectedMemberType === item?.label;
          } else {
            return item?.defaultProperties?.defaultSelection;
          }
        })?.[0]?.label;
    setADTState(ADTregistrySelected);
    memberTypeHandler(ADTregistrySelected);
    
    getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: ADTregistrySelected,
      filterBody: filteredInfo,
    });

    getCloumnList({});
    // getMasterData();
    if(!isCentric){
      fetchNotifications();
    }
    // getFilters();
    membersService.setsingleMember = true;
  };

  useEffect(() => {
    if (memberListRegistryDropDown && memberListRegistryDropDown?.length === 0) {
      getMemberListRegistryDropDown();
    } else {
      initialDataLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(()=>{
    if(config){
      getMasterData()
    }
  },[config])

  useEffect(() => {
    const ADTregistrySelected =
      comingFromPopulationInst || comingFromMeasures || comingFromChronicBreakdown
        ? find(registryDropDownAndTableConfig, { label: "Filtered Members" })?.label
        : registryDropDownAndTableConfig?.filter((item: any) => {
            if (selectedMemberType) {
              return selectedMemberType === item?.label;
            } else {
              return item?.defaultProperties?.defaultSelection;
            }
          })?.[0]?.label;

    // This effect will be triggered whenever sort changes
    getMembers({
      page: getCurrentPage(),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: ADTregistrySelected,
      filterBody: filteredInfo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortType]);

  // Getting Member list Data refresh Status ( if job is running member list becomes slow )
  useEffect(() => {
    const interval = setInterval(() => {
      getMemberListDataRefreshStatus();
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    return () => {
      // Clean up the interval when the component unmounts
      clearInterval(interval);
    };
  }, []);

  const getMemberListDataRefreshStatus = () => {

    const token = storage.get("_token");
    const refreshToken = storage.get("_refreshToken");
    if(token && refreshToken) {
      memberService.fetchMemberListDataRefreshStatus().subscribe({
        next: (res: any) => {
          setDataRefreshStatus(res?.data?.data);
        },
      });
    }
  };
  ////////////////////////////////////////////////////////////////////////////////////////////

  const collapseCaseStatus = () => {
    setCaseStatusExpanded(false);
    setTableConfigData((old: any) =>
      old?.map((h: any) => {
        if (expandableColumns.includes(h.dataIndex)) h.title = getExpandableColumnTitle(h.displayName);
        else if (h.dataIndex === "case_last_action_date") h.isSelected = false;
        else if (h.dataIndex === "prev_care_manager_name") h.isSelected = false;
        return h;
      })
    );
  };

  //@author : Kishore Pantra
  //@desc : this function is used to get the current page of assigned or admission or discharged if adtstate
  //is not updated due to useState we pass adtflag to the function
  const getCurrentPage = (adtFlag?: any, filters = filteredInfo) => {
    const adtvalue = adtFlag === "" ? adtFlag : adtFlag || adtState;

    if(registryDependentMemberListPageStore?.[adtvalue]) {
      return get(registryDependentMemberListPageStore, adtvalue);
    } else {
      let tempRegistryPages = cloneDeep(registryDependentMemberListPageStore);
      tempRegistryPages = tempRegistryPages ? tempRegistryPages : {}
      tempRegistryPages[adtvalue] = 1;
      setRegistryDependentMemberListPageStore(tempRegistryPages);
      return 1;
    }
    
  };
  const expandColumn = (header: string) => {
    setTableConfigData((old: any) =>
      old?.map((item: any) => {
        if (expandableColumns.includes(item.dataIndex) && item.displayName === header) {
          if (item.expanded) {
            item.expanded = false;
            item.children = [];
          } else {
            item.expanded = true;
            item.children = [...item.subColumns];
          }
        }
        return item;
      })
    );
  };

  const getExpandableColumnTitle = (header: string) => {
    return (
      <div className="expandable-column-header">
        {header.toUpperCase()}
        <i className="btn-icon icon-chevron-solid-v-col" onClick={() => expandColumn(header)}></i>
      </div>
    );
  };

  // let sub: any;

  // const applyColumnSearch = (columnName: any, text: any, resetFlag ?: boolean , displayName ?: string , searchInfo = columnSearchInfo) => {
  //     text = text
  //     ? text
  //     : columnName === "admissionsRiskScore"
  //     ? get(searchInfo, [columnName], "").replace("%", "")
  //     : columnName === "lastYearCost"
  //     ? get(searchInfo, [columnName], "").replace(",", "").replace("$", "")
  //     : get(searchInfo, [columnName], "");
  //   let sBy: string | undefined = undefined;
  //   let sKeyword = undefined;
  //   if (columnName?.trim() !== "" || text.trim() !== "") {
  //     sBy = columnName;
  //     sKeyword = text;
  //   }
  //   sub?.unsubscribe();
  //   setSearchByPage(1);

  //   let searchOn = filter(searchKeywordNew, item => {
  //     return item.columnName !== sBy;
  //   });
  //   let x  = resetFlag === true ? searchOn : [...searchOn, { columnName: sBy, displayName : displayName , type : 'ACTN-FREETXT' , searchText: sKeyword }];
  //   setSearchKeywordNew(x);
  //   sub = getMembers(
  //     {page:getCurrentPage("", filteredInfo, columnName),
  //       limit:  rowsPerPage,
  //       sortBy: sortBy,
  //       sortType: sortType,
  //       searchOn: x,
  //       reminderStartDate: reminderStartFilter,
  //       reminderEndDate: reminderEndFilter,
  //       adtFlag: adtState,
  //       filterBody: filteredInfo}
  //   );
  //   setSearchBy(sBy);
  //   setSearchKeyword(sKeyword);
  // };

  const viewType = "tableView";
  const [isOpenDrawer, toggleDrawerHandler] = useState(false);
  const [isDownloadDrawerOpen, setDownloadDrawer] = useState(false);
  const [drawerViewtype, setDrawerViewtype] = useState(layerViews[0]);
  const selectedMemberType = useMemberListStore((state) => state.selectedMemberType);
  const memberTypeHandler = useMemberListStore((state) => state.memberTypeHandler);
  const [selectionType] = useState<"checkbox" | "radio">("checkbox");
  const [selectedRows, setSelectedRows] = useState<any>({
    selectedRowKeys: [],
  });
  const [caseStatusExpanded, setCaseStatusExpanded] = useState(false);
  const [tempTableConfig, tempTableConfigHandler] = useState<any>();
  const filtersData = useMemberListStore((state) => state.filtersData);
  // const filtersDataHandler = useMemberListStore((state) => state.filtersDataHandler);
  const tempFiltersData = useMemberListStore((state) => state.tempFiltersData);
  const tempFiltersDataHandler = useMemberListStore((state) => state.tempFiltersDataHandler);
  const isResetLayoutEnable = useMemberListStore((state) => state.isResetLayoutEnable);
  const [caseStatusPosition] = useState<any>({
    left: 0,
    width: 0,
  });
  const [downloadType, setDownloadType] = useState();

  // Notification state
  const [newNotificationFlag, setNewNotificationFlag] = useState(false);

  const [filtersLoader, setFiltersLoader] = useState(false);
  const [keyInsightLoader, setInsightLoader] = useState(false);
  const [toggleSelectAllRow, setToggleSelectAllRow] = useState(false);
  const [totalCount, setTotalCount] = useState();
  const [columnLoader, setColumnLoader] = useState<boolean>(false);

  const updateSelection = ({ memdata = data, toggleRequired = true }) => {
    onSelectChange(memdata.map((p: any) => p.patient_id));
    if (toggleRequired) {
      setToggleSelectAllRow(!toggleSelectAllRow);
    }
  };

  const cancelSelection = (event: any) => {
    setDownloadType(undefined);
    onSelectChange([]);
    setToggleSelectAllRow(!toggleSelectAllRow);
  };

  const fetchNotifications = () => {
    const page = 1;
    const filter = {
      fromDate: moment.utc(moment(`${moment().format("YYYY-MM-DD")} 23:59:59`).utc())
    };
    memberService.fetchAllNotifications(page, filter).subscribe({
      next: (v) => {
        let data = v.data.data;
        let notificationsArray =  uniqBy([...(notificationsData?.notifications ? notificationsData?.notifications : []) , ...data.notifications] , 'notificationID').sort((a, b) => {
          const dateA = moment(a.notificationDate);
          const dateB = moment(b.notificationDate);
          return dateB.diff(dateA); // Compare in descending order
        })
        setNotificationsData({...(notificationsData ? notificationsData : {}) , notifications : notificationsArray , totalNotifications : data.readCount + data.unreadCount , latest_datetime : get(notificationsArray , '[0].notificationDate') , datetime : get(notificationsArray , '[0].notificationDate')});
        setNewNotificationFlag(v.data.data.unreadCount > 0);
      },
      error: (e) => {
        console.log(e);
      },
    });
  };

  const selectionMenu = (
    <Menu>
      <div className="table-selection-options">
        <Radio.Group value={downloadType ? downloadType : "current"} onChange={(e) => setDownloadType(e.target.value)}>
          <Space direction="vertical">
            <Radio value="current" className="f16 clr-n100 selection-options">
              All patients from the current page
            </Radio>
            <Radio value="all" className="f16 clr-n100 selection-options">
              All patients across all pages
            </Radio>
          </Space>
        </Radio.Group>
        <div className="selection-actions">
          <Button className="cancel-btn" onClick={cancelSelection}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => updateSelection({})}>
            Continue
          </Button>
        </div>
      </div>
    </Menu>
  );

  const { selectedRowKeys } = selectedRows;

  const onSelectChange = (selectedRowKeys: any) => {
    setSelectedRows({ selectedRowKeys });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnTitle: (
      <div className="selection-column">
        <Dropdown overlay={toggleSelectAllRow ? selectionMenu : <></>} visible={toggleSelectAllRow}>
          <Checkbox
            checked={selectedRowKeys?.length === data?.length}
            onClick={() => setToggleSelectAllRow(!toggleSelectAllRow)}
            onChange={() => onSelectChange(selectedRowKeys?.length === data?.length ? [] : data.map((d) => d.key))}
            indeterminate={selectedRowKeys.length > 0 && selectedRowKeys?.length !== data.length}
          />
        </Dropdown>
      </div>
    ),
  };

  const toggleDrawer = () => {
    toggleDrawerHandler(!isOpenDrawer);
  };

  const toggleDownloadDrawer = () => {
    setDownloadDrawer((prevDrawerState) => !prevDrawerState);
  };

  const toggleDrawerViewType = (type: any) => {
    const tempType = cloneDeep(type);
    if (type?.key === "columnConfig") {
      // const clientMap: any = columnLayoutLabelMap.get(clientName);
      // let label = 'Default'
      // if (clientMap) {
      //   label = clientMap[selectedMemberType] || "Default";
      // } else {
      //   const defaultMap: any = columnLayoutLabelMap.get("Default");
      //   label =  defaultMap[selectedMemberType] || "Default";
      // }")
      const adtOptionObject = find(registryDropDownAndTableConfig, { label: selectedMemberType });
      const label = get(adtOptionObject, "columnConfigPageName") !== "Member List" ? adtOptionObject?.label : "Default";
      const tempLabel = `Select columns: ${label} Layout`;
      tempType.label = tempLabel;
    }
    setDrawerViewtype(tempType);
    toggleDrawer();
  };

  const updateColumns = () => {
    const columns: any = [];
    tempTableConfig?.forEach((col: any) => {
      const arr = columnListdata.filter((cld: any) => {
        return col.dataIndex === cld.dataIndex;
      });
      if (arr && arr.length > 0 && col.isSelected) columns.push({ column_id: arr[0].column_id, order: col.sequence });
    });

    // Extracting column Page name
    const selectedRegistryDropDown = find(registryDropDownAndTableConfig, { label: adtState });
    const columnPageName = selectedRegistryDropDown?.columnConfigPageName || "Member List";

    memberService.updateColumns(columns, columnPageName).subscribe({
      next: (v) => {
        setTableConfigData(tempTableConfig);
        getCloumnList({});
      },
      error: (e) => {},
    });
  };

  //this function will check if the upcoming columns has all filter applied columns or not
  const columnFilterCheck = ({ config = tempTableConfig, filterInfo = filteredInfo, action = "", data = {}, onOk = undefined }): boolean => {
    let flag = false;
    let cols: Array<string> = [];
    Object.keys(filterInfo).forEach((column_key: string) => {
      let configCol = getColumn(config, column_key, true);
      let prevConfigCol = getColumn(tempTableConfig, column_key, true);
      if (!isEmpty(configCol) && !configCol.isSelected && prevConfigCol?.isSelected) {
        cols.push(configCol.displayName);
        setIsFilterModal({ open: true, action: action, data: data, onOk: onOk, colNames: cols });
        flag = true;
      }
    });
    return flag;
  };

  const updateTableConfig = () => {
    updateColumns();
    toggleDrawer();
  };

  const resetDefaultTableConfig = ({ action = "default" }) => {
    if (action === "default") {
      let columnData = cloneDeep(tempTableConfig);
      columnData = columnData?.map((col: any) => {
        if (col.isDraggable) {
          if (col.isDefault === "Y") {
            col.sequence = col.defaultDisplayOrder;
            col.isSelected = true;
            col.draggable = true;
          } else {
            col.sequence = 0;
            col.isSelected = false;
            col.draggable = false;
          }
        } else {
          col.sequence = col.order;
          col.isSelected = true;
          col.draggable = true;
        }
        return col;
      });
      colFilterMiddleWare(columnFilterCheck, { config: columnData }, () => {
        tempTableConfigHandler(columnData);
      });
    } else {
      tempTableConfigHandler(tableConfigData);
    }
  };

  // Update data in temp storage to update later
  const tableConfigCallback = (data: any) => {
    tempTableConfigHandler(data);
  };

  const setPageSize = (e: number) => {
    setRowsPerPage(e);
    resetFilterPage({});
    //Call API Again with page size as e and page as currentPage

    getMembers({
      page: 1,
      limit: e,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: adtState,
      filterBody: filteredInfo,
    });
  };

  const changeCurrentPage = (val: any) => {
    //Call API Again with page size as rowsPerPage and page as val
    const tempRegistryPages = cloneDeep(registryDependentMemberListPageStore);
    tempRegistryPages[adtState] = val;
    setRegistryDependentMemberListPageStore(tempRegistryPages);

    getMembers({
      page: val,
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: adtState,
      filterBody: filteredInfo,
    });
  };

  const changeCurrentPageToNext = ({ direction, callBack, currentPages }: any) => {
    const val = direction === "right" ? currentPages + 1 : currentPages - 1;
    //Call API Again with page size as rowsPerPage and page as val
    const tempRegistryPages = cloneDeep(registryDependentMemberListPageStore);
    tempRegistryPages[adtState] = val;
    setRegistryDependentMemberListPageStore(tempRegistryPages);

    getMembers({
      page: val,
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: adtState,
      filterBody: filteredInfo,
      callBack: callBack,
    });
  };

  setNextFunc(({ direction, callBack, currentPages = getCurrentPage() }: any) => {
    changeCurrentPageToNext({ direction, callBack, currentPages });
  });

  function getSortandADTMap(flag : any, currentSortBy : string, currentSortType : any) {
    const selectedAdtOption = find(registryDropDownAndTableConfig, { label: flag });
    const adtMapping = flag === "Filtered Members" ? "" : selectedAdtOption?.value;
    let { columnKey , order } = selectedAdtOption?.defaultProperties?.sort || {};
    const sortColumn = currentSortBy ? currentSortBy : columnKey;
    let sortOrder = currentSortType;
    if (sortColumn) {
        sortOrder = sortOrder ? sortOrder : order === "ascend" ? ASC : order === "descend" ? DESC : order;
    }
    return { sortColumn, sortOrder, adtMapping };
  }

  const getMembers = ({
    page,
    limit,
    sortBy,
    sortType,
    searchOn,
    reminderStartDate,
    reminderEndDate,
    adtFlag,
    filterBody = filteredInfo,
    patientListForFilter = undefined,
    callBack,
    dataFlag = getDataFlag(),
  }: any) => {
    if (!dataFlag) {
      setLoaderFlag(true);
      patientListForFilter = 
        adtFlag === "Filtered Members" && comingFromMeasures ?
        measuresPatientIds :
        adtFlag === "Filtered Members" && comingFromChronicBreakdown ?
        chronicPatientIds 
        : adtFlag === "Filtered Members"
        ? pIds
        : patientListForFilter && patientListForFilter?.length > 0
        ? patientListForFilter
        : ADTPatientIdList?.[adtFlag]
        ? ADTPatientIdList?.[adtFlag]
        : [];

      const {sortColumn, sortOrder,  adtMapping} = getSortandADTMap(adtFlag , sortBy , sortType);

      return memberService.members(page, limit, sortColumn, sortOrder, searchOn, reminderStartDate, reminderEndDate, adtMapping, filterBody, patientListForFilter).subscribe({
        next: (v) => {
          setMemberListData([]);
          const memberData = v?.data?.data?.patientList.map((patient: any) => {
            const old = { ...patient };
            old.reminders = patient.reminders.map((rem: any) => {
              const oldRem = { ...rem };
              oldRem.reminderStartDate = rem.reminderStartDate;
              oldRem.reminderEndDate = rem.reminderEndDate;
              return oldRem;
            });
            old.key = patient.patient_id;
            return old;
          });
          setTotalCount(v?.data?.data?.totalCount);
          if (v?.data?.data?.totalCount === 0) {
            setNoData(true);
          } else {
            setNoData(false);
          }
          setMemberListData(memberData);
          //@Author : kishore
          //@desc : to check the patients when we select All patients across all pages when we move from one page to another
          if (downloadType === "all") {
            updateSelection({ memdata: memberData, toggleRequired: false });
          }
          setTotalPages(v.data.data.totalCount);
          setLoaderFlag(false);
          membersService.setMemberListFn(memberData);
          if (callBack) {
            callBack();
          }
        },
        error: (e) => {
          console.log(e);
          setLoaderFlag(false);
        },
      });
    }
  };

  const formatColumnData = (colData: any) => {
    setColumnListData(colData);
    getOptions(getDropdownFilters({ colData: colData }));
    const tcf = cloneDeep(colData.filter((col: any) => col.isDisplay === "Y"))?.map((item: any) => {
      const columnWidth = item.columnWidth && item.columnWidth !== "null" ? Number(item.columnWidth) : 200;

      let newItem = {
        ...item,
        title:  item.tableHeaderName,
        label: item.displayName,
        sequence: item.order,
        width: columnWidth,
        ellipsis: true,
        // ellipsis: {
        //   showTitle: false,
        // },
        colorCoded: false,
        render: (text: any) => (
          <Tooltip placement="topLeft" title={text}>
            {text}
          </Tooltip>
        ),
      };

      // const defaultColumnProperty = find(registryDropDownAndTableConfig, { label: adtFlag });

      if (["priority", "patient_name"].includes(newItem.dataIndex)) {
        newItem = { ...newItem, fixed: true };
      }

      if (expandableColumns.includes(newItem.dataIndex)) {
        newItem.title = getExpandableColumnTitle(newItem.displayName);
        newItem.subColumns.unshift({
          key: newItem.dataIndex,
          title: newItem.tableHeaderName,
          label: newItem.displayName,
          dataIndex: newItem.dataIndex,
          className: "member-long-column",
          displayName: newItem.dataIndex === "risk_category" ? "Current Risk Level" : newItem.displayName,
          tableHeaderName: newItem.dataIndex === "risk_category" ? "CURRENT RISK LEVEL" : newItem.tableHeaderName,
          // order: 10,
          isSelected: true,
          type: newItem.type,
          dropdown_key: newItem.dropdown_key,

          // Color code for columns ( If columns needed color code please add column which needs color coded)
          colorCoded: ["prev_risk_category", "risk_category"].includes(newItem.dataIndex),
        });
        newItem = { ...newItem, expanded: false };
      }
      if (newItem.subColumns.length > 0) {
        let updatedSubColumns = cloneDeep(newItem.subColumns).map((subItem: any) => {
          let newSubColumnItem = {
            ...subItem,
            title: subItem.tableHeaderName,
            label: subItem.displayName,
            sequence: subItem.order,
            width: columnWidth,
            className: "member-long-column",
            colorCoded: ["prev_risk_category", "risk_category"].includes(newItem.dataIndex),
          };
          return newSubColumnItem;
        });
        newItem.subColumns = updatedSubColumns;
      }
      if (item.type === "ACTN-DOLLAR-CRITERIA") {
        newItem = { ...newItem, className: item.format_type === "RIGHT" || item.format_type === null ? "textAlignRight member-col-long" : "member-col-long" };
      }

      if (["risk_category", "admissions_risk", "hcc_risk", "current_year_hcc_risk_score", "previous_year_hcc_risk_score", "hcc_risk_roaster", "prev_risk_category", "utilization_risk", "readmission_risk", "eol_score"].includes(item.dataIndex)) {
        newItem = { ...newItem, colorCoded: true };
      }
      if (["previous_year_hcc_risk_score", "current_year_hcc_risk_score", "hcc_demographic_factor"].includes(item.dataIndex)) {
        const currentYear = new Date().getFullYear();
        const nextYear = currentYear + 1;
        const year = item.dataIndex === "previous_year_hcc_risk_score" ? currentYear : nextYear;
        newItem = { 
          ...newItem, 
          label: `${item.displayName} ${year}`, 
          tableHeaderName: `${item.tableHeaderName} ${year}` 
        };
      }
      if (["patient_name", "caremgr_name", "pcp_name", "office_name", "member_caseload_reason", "admitted_facility" , 'region_name', "eol_user_name"].includes(item.dataIndex)) {
        newItem = { ...newItem, className: "member-table-name-column mw-member-col" };
      }
      if (["program"].includes(item.dataIndex)) {
        newItem = { ...newItem, className: item.format_type === "LEFT" || item.format_type === null ? "member-table-name-column mw-member-col" : "mw-member-col" };
      } else if (
        [
          "risk_category",
          "action",
          "admissions_risk_score",
          "office_name",
          "last_year_cost",
          "total_cost_YTD",
          "current_year_cost",
          "hcc_risk",
          "member_caseload_status",
          "patient_eol_status",
        ].includes(item.dataIndex)
      ) {
        if (item.dataIndex === "action") {
          newItem = {
            ...newItem,
            render: (age: any, record: any) => {
              return <TableActions data={record} getMasterData={getMasterData} />;
            },
          };
        } else if (item.dataIndex === "member_caseload_status") {
          newItem = {
            ...newItem,
            render: (text: any, record: any) => {
              return (
                <CanEdit hide={false}>
                  {" "}
                  <CheckBox
                    label={"Caseload"}
                    className={record.member_caseload_status === "SYSTEM" ? "light-background" : ""}
                    classificationType={"CASELOAD"}
                    currentMember={record}
                    dataIndex={"member_caseload_status"}
                    serviceCallForCaseLoadMasterData={serviceCallForCaseLoadMasterData}
                    patientId={record.patient_id}
                    getMasterData={getMasterData}
                  ></CheckBox>
                </CanEdit>
              );
            },
          };
        } else if (item.dataIndex === "patient_eol_status") {
          newItem = {
            ...newItem,
            render: (text: any, record: any) => {
              return (
                <CanEdit hide={false}>
                  {" "}
                  <CheckBox
                    label={"End Of Life Patient"}
                    classificationType={"EOL"}
                    currentMember={record}
                    dataIndex={"patient_eol_status"}
                    serviceCallForCaseLoadMasterData={false}
                    patientId={record.patient_id}
                    getMasterData={getMasterData}
                  ></CheckBox>
                </CanEdit>
              );
            },
          };
        }
        newItem = { ...newItem, className: `${newItem.className ? newItem.className : ""} member-long-column mw-member-col` };
      } else if (
        [
          ...expandableColumns,
          "case_last_action_date",
          "Utilization_Measures_curr_period",
          "Utilization_Measures_curr_year",
          "admission_date",
          "discharge_date",
          "terminal_disease_cnt",
          "er_visit_start_date",
          "er_visit_end_date",
        ].includes(item.dataIndex)
      ) {
        newItem = { ...newItem, className: "member-long-column" };
      }
      return newItem;
    });
    setTableConfigData(tcf);
    tableConfigCallback(tcf);
    setDownloadColData([...tcf, ...colData.filter((col: any) => col.isDisplay === "N" && col.tableHeaderName)]);
    setIsDrawerLoading(false);
  };

  const getCloumnList = ({ adtFlag = adtState, action = "", filtersData = filteredInfo, admissionErDischargeOptionPayload = null }) => {
    console.log("Members.getCloumnList");
    setColumnLoader(true);
    const selectedRegistryDropDown = find(registryDropDownAndTableConfig, { label: adtFlag });
    const columnPageName = selectedRegistryDropDown?.columnConfigPageName || "Member List";
    memberService.getColumnsList(columnPageName).subscribe({
      next: (v) => {
        const colData = v?.data?.data?.columns;
        if (!action) {
          formatColumnData(colData);
        } else if (!columnFilterCheck({ config: colData, filterInfo: filteredInfo, action: "adtClick", data: { adtFlag: adtFlag, colData: colData } })) {
          formatColumnData(colData);
          if (action === "adtClick") {
            handleAdtData(adtFlag, admissionErDischargeOptionPayload);
          }
        }
        setColumnLoader(false);
      },
      error: (e) => {
        setColumnListData([]);
        setDownloadColData([]);
        setIsDrawerLoading(false);
        setColumnLoader(false);
      },
    });
  };

  // Getting Master data

  const getMasterData = () => {

    const kpiCardsConfig: any = {};
    if (config) {
      config.member_list["KPI Cards"].optionValues
        .filter((opt: any) => opt.show)
        .forEach((option: any) => {
          kpiCardsConfig[option.name] = option.show;
        });
    }

    if (kpiCardsConfig["Case status Distribution"] && kpiCardsConfig["Predicted Cost Saving"]) {
      memberService.getMasterData().subscribe({
        next: (v) => {
          setMasterData(v?.data?.data);
          setIsloadingCaseStatus(false);
        },
        error: (e) => {
          console.log(e);
        },
      });
    }
  };
  

  const serviceCallForCaseLoadMasterData = useCallback(() => {
    return;
    /*
    Dont need as we not showing case load count in KPI Card
    membersService.getMasterDataForCaseLoad().subscribe({
      next: (v: any) => {
        const response = v?.data;
        // const response = CaseLoad;
        setCaseLoadCount(response?.data?.totalCaseLoad?.count);
        setCaseLoadLoader(false);
      },
      error: (e: any) => {
        console.log(e ? e : "empty");
        setCaseLoadLoader(false);
      },
    })
    */
  }, [setCaseLoadLoader]);

  const getDataFlag = (flag: string = adtState, admissionErDischargeOptionPayload: any = masterDataForAdmitAndDischarge) => {
    let dataFlag =
      flag === "Admissions" && (admissionErDischargeOptionPayload ? admissionErDischargeOptionPayload?.totalHospitalAdmission?.patientHospitalAdmissionList?.length <= 0 : ADTPatientIdList?.[flag]?.length <= 0)
        ? true
        : flag === "Discharges" && (admissionErDischargeOptionPayload ? admissionErDischargeOptionPayload?.membersDischarged?.patientHospitalDischargeList?.length <= 0 : ADTPatientIdList?.[flag]?.length <= 0)
        ? true
        : flag === "Inpatients" &&
          (admissionErDischargeOptionPayload ? admissionErDischargeOptionPayload?.totalHospitalInpatient?.patientHospitalInpatientList?.length <= 0 : ADTPatientIdList?.[flag]?.length <= 0)
        ? true
        : flag === "ER" && (admissionErDischargeOptionPayload ? admissionErDischargeOptionPayload?.totalErCount?.patientHospitalEmergencyList?.length <= 0 : ADTPatientIdList?.[flag]?.length <= 0)
        ? true
        : false;

    return dataFlag;
  };

  const handleAdtData = (flag: string, admissionErDischargeOptionPayload: any = null) => {
    const dataFlag = getDataFlag(flag, admissionErDischargeOptionPayload);
    setNoData(dataFlag);
    setADTState(flag);
    memberTypeHandler(flag);
    let patientListForFilter = [];
    patientListForFilter =
      flag === "Admissions"
        ? admissionErDischargeOptionPayload
          ? admissionErDischargeOptionPayload?.totalHospitalAdmission?.patientHospitalAdmissionList
          : masterDataForAdmitAndDischarge?.totalHospitalAdmission?.patientHospitalAdmissionList
        : flag === "Discharges"
        ? admissionErDischargeOptionPayload
          ? admissionErDischargeOptionPayload?.membersDischarged?.patientHospitalDischargeList
          : masterDataForAdmitAndDischarge?.membersDischarged?.patientHospitalDischargeList
        : flag === "Inpatients"
        ? admissionErDischargeOptionPayload
          ? admissionErDischargeOptionPayload?.totalHospitalInpatient?.patientHospitalInpatientList
          : masterDataForAdmitAndDischarge?.totalHospitalInpatient?.patientHospitalInpatientList
        : flag === "ER"
        ? admissionErDischargeOptionPayload
          ? admissionErDischargeOptionPayload?.totalErCount?.patientHospitalEmergencyList
          : masterDataForAdmitAndDischarge?.totalErCount?.patientHospitalEmergencyList
        : [];
    setPatientIdList(patientListForFilter);
    getMembers({
      page: Object.keys(filteredInfo).length > 0 ? 1 : admissionErDischargeOptionPayload ? 1 : getCurrentPage(flag),
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: flag,
      filterBody: filteredInfo,
      patientListForFilter: patientListForFilter?.length > 0 ? patientListForFilter : [],
      dataFlag,
    });

    if (dataFlag) {
      setMemberListData([]);
    }
  };

  const handleAdtClicks = (value: string, admissionErDischargeOptionPayload?: any) => {
    getCloumnList({ adtFlag: value, action: "adtClick", admissionErDischargeOptionPayload: admissionErDischargeOptionPayload });
  };

  // const getFilters = () => {
  //   if (filtersData.length === 0 || tempFiltersData.length === 0) {
  //     setFiltersLoader(true);
  //     memberService.getFilters("Member List").subscribe({
  //       next: (v) => {
  //         const colData = v?.data?.data?.result?.map((item: any) => {
  //           item.selectedOptions = [];
  //           return item;
  //         });
  //         filtersDataHandler(colData);
  //         tempFiltersDataHandler(colData);
  //         setFiltersLoader(false);
  //       },
  //       error: (e) => {
  //         filtersDataHandler([]);
  //         tempFiltersDataHandler([]);
  //         setFiltersLoader(false);
  //       },
  //     });
  //   }
  // };

  const drawerFooter = () => {
    return (
      <div className="config-footer">
        {drawerViewtype.key === layerViews[1].key ? (
          <div className="config-footer_message">
            {" "}
            ( <span style={{ color: "red" }}>*</span> ) Filters Applied
          </div>
        ) : (
          false
        )}
        <div style={{ textAlign: "right" }} className="cancel-button">
          <Button disabled={isDrawerLoading} type="text" size="large" onClick={onCloseConfigDrawer}>
            Cancel
          </Button>

          {drawerViewtype.key === layerViews[0].key ? (
            <Button type="primary" onClick={() => applyTableFilter({})} className="apply-filter" style={{ height: "40px" }}>
              Apply Filter
            </Button>
          ) : (
            <Button disabled={isDrawerLoading} type="primary" onClick={updateTableConfig} style={{ height: "40px" }}>
              Update Table
            </Button>
          )}
        </div>
      </div>
    );
  };

  const clearFilterHandler = () => {
    const cc = cloneDeep(filtersData);
    const filters = cc.map((item: any) => {
      item.selectedOptions = [];
      return item;
    });
    tempFiltersDataHandler(filters);
    applyTableFilter({ _filtersData: filters, _toggledrawer: false });
  };

  const resetAllToDefaultTableConfig = (config?: any) => {
    setIsDrawerLoading(true);
    const columnPageName = "Member List-Reset All";
    memberService.updateColumns([], columnPageName).subscribe({
      next: (v) => {
        setTableConfigData(config ? config : tempTableConfig);
        getCloumnList({ filtersData: {} });
      },
      error: (e) => {},
    });
  };

  const drawerHeader = () => {
    return drawerViewtype.key === layerViews[0].key ? (
      <Button type="link" onClick={clearFilterHandler} className="clr-e100" style={{ fontWeight: "normal" }}>
        Clear All
      </Button>
    ) : (
      <div style={{ display: "flex" }}>
        <Button
          type="link"
          className={classNames({
            "reset-layout": true,
            "reset-layout__enabled": !isResetLayoutEnable,
            "reset-layout__disabled": isResetLayoutEnable,
          })}
          disabled={isResetLayoutEnable || isDrawerLoading}
          onClick={() => resetDefaultTableConfig({})}
        >
          Reset Layout
        </Button>
        <Button type="link" className="reset-all" onClick={() => handleResetAll()} disabled={isDrawerLoading}>
          Reset All
        </Button>
      </div>
    );
  };

  const handleResetAll = () => {
    filterCount({ filteredInfo }) > 0
      ? setIsFilterModal({
          open: true,
          action: "resetAll",
          data: {},
          onOk: () => {
            setFilteredInfo({});
            getFilteredTableData({ filters: {} });
            resetFilterPage({});
            resetAllToDefaultTableConfig();
          },
          colNames: [],
        })
      : resetAllToDefaultTableConfig();
  };

  //function to reset filter page on addition or removal of filters
  const resetFilterPage = ({ page = 1 }) => {
    const tempRegistryPages = cloneDeep(registryDependentMemberListPageStore);
    Object.keys(tempRegistryPages)?.forEach((item:any) => {
      tempRegistryPages[item] = page;
    })
    setRegistryDependentMemberListPageStore(tempRegistryPages);
  };

  const applyTableFilter = ({ _filtersData = tempFiltersData, _toggledrawer = true }) => {
    let _filteredInfo: any = cloneDeep(filteredInfo);
    for (const [key, value] of Object.entries<any>(_filtersData)) {
      if (value?.length > 0) {
        _filteredInfo[key] = { type: "ACTN-MSELDRPDWN", displayName: getColumn(tableConfigData, key)?.displayName, value: value };
      } else if (_filteredInfo[key]?.type === "ACTN-MSELDRPDWN") {
        delete _filteredInfo[key];
      }
    }
    setFilteredInfo({ ..._filteredInfo });
    resetFilterPage({});
    getFilteredTableData({ filters: { ..._filteredInfo } });
    if (_toggledrawer) {
      toggleDrawer();
    }
  };

  const filtersChangeHandler = (index: number, selectedArray: any) => {
    let copyFilterData: any = cloneDeep(tempFiltersData);
    copyFilterData[index].selectedOptions = [...selectedArray];
    tempFiltersDataHandler(copyFilterData);
  };

  const drawerView = () => {
    return drawerViewtype.key === layerViews[0].key ? (
      <Filters data={tempFiltersData} changeHandler={filtersChangeHandler} />
    ) : (
      <TableConfig configUpdatehandler={tableConfigCallback} tableConfigData={cloneDeep(tempTableConfig)} columnFilterCheck={columnFilterCheck} filteredInfo={filteredInfo} />
    );
  };

  const onCloseConfigDrawer = () => {
    toggleDrawer();
    resetDefaultTableConfig({ action: "revert" });
    if (isFiltersApplied()) {
      tempFiltersDataHandler(filtersData);
    }
  };

  const memberTypeChangeHandler = (value: string) => {
    // if (option) {
    //   handleAdtClicks(option, value);
    // } else {
    //   setNoData(false);
    //   handleAdtClicks("", value);
    // }
    handleAdtClicks(value);
    //Removing ascending sort order for priority when registry dropdown is selected Last Reviwed members
  };

  // FILTERS: this function can be optimized and reused
  // when api will be called with selected all filters
  const isFiltersApplied = () => {
    const copyData: TFilterOption[] = cloneDeep(tempFiltersData);
    return copyData?.filter((item: TFilterOption) => item?.selectedOptions && item?.selectedOptions?.length > 0).length > 0;
  };

  const handleChange = (sorter: any) => {
    let sortType = sorter?.order === "ascend" ? ASC : sorter?.order === "descend" ? DESC : sorter?.order;
    setSortByFilter(sorter?.columnKey);
    setSortTypeFilter(sortType);

};

  function onRowClick(underLineText: boolean, text: string) {
    if (underLineText) {
      setHospitalMappingEditModeOpenedFrom(HOSPITAL_LIST_AND_CM_USER_OPENED_FROM.Members);
      setShowMemberDetailsModal(true);
      setSelectedHospitalListFacility(text);
    }
  }

  const highlighterDiv = (item: any, text: any, record: any, searchWord: any) => {
    switch (item.dataIndex) {
      case "patient_name":
        return (
          <span className="clr-blueLink f16 w500" style={{ cursor: "pointer" }} onClick={() => goToMemberDetails(record)}>
            <Tooltip placement="topLeft" title={text}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ?? "-"} />
            </Tooltip>
          </span>
        );
      case "caremgr_name":
        return text === "Unassigned" ? (
          <div className="hover-btn">
            <span className="f14 w500 italic">
              <Tooltip placement="topLeft" title={text}>
                <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ?? "-"} />
              </Tooltip>
            </span>
            <Button icon={<span className="icon icon-plus"></span>} type="text" danger>
              Assign to self
            </Button>
          </div>
        ) : (
          <div className="user-info">
            {/* <span className="avatar">
              <img style={{ height: "22px" }} src={record.caremgr_profile} alt="img" />
            </span> */}
            <span className="info">
              <Tooltip placement="topLeft" title={text}>
                <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ?? "-"} />
              </Tooltip>
            </span>
          </div>
        );
      case "patient_case_status":
        const status = filterDropdowns?.PatientCaseStatusFilterLst?.find((item:any) => item.label === text)
        const classNameForPatientStatus = classNames("f14 w500");
        return (
          <span className={classNameForPatientStatus} style={{ color: status?.color }}>
            <Tooltip placement="topLeft" title={text}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ?? "-"} />
            </Tooltip>
          </span>
        );
      case "last_year_cost":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? fixedDecimal(text, 2) : ""}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? `$ ${fixedDecimal(text, 2)} ` : "-"} />
            </Tooltip>
          </span>
        );
      case "total_cost_YTD":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? fixedDecimal(text, 2) : ""}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? `$ ${fixedDecimal(text, 2)} ` : "-"} />
            </Tooltip>
          </span>
        );
      case "pmpm":
      case "current_year_cost":
      case "current_year_medical_cost":
      case "current_year_pharmacy_cost":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? fixedDecimal(text, 2) : ""}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? `$ ${fixedDecimal(text, 2)} ` : "-"} />
            </Tooltip>
          </span>
        );
      case "patient_date_of_birth":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text, "createDateFormat") : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text, "createDateFormat")}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "admission_date":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false) : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false)}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "discharge_date":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false) : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false)}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "er_visit_start_date":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false) : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false)}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "er_visit_end_date":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false) : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false)}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "case_last_action_date":
        return (
          <span>
            <Tooltip placement="topLeft" title={text ? formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false) : ""}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchWord]}
                autoEscape
                textToHighlight={text ? `${formatDisplayDate(text.toString(), "reminderDateActualFormat", "defaultDateFormatWithTime", false)}` : "-"}
              />
            </Tooltip>
          </span>
        );
      case "admitted_facility":
        let underLineText: boolean = true;
        if (text === "NA") {
          underLineText = false;
        }
        return clientName !== "Millennium" ? (
          <div
            style={{ textDecoration: underLineText ? "underline" : "none", color: underLineText ? "#0072E3" : "#7A869A", cursor: underLineText ? "pointer" : "" }}
            onClick={() => {
              onRowClick(underLineText, text);
            }}
          >
            <Tooltip placement="topLeft" title={text?.toString() ?? ""}>
              <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text?.toString() ?? "-"} />
            </Tooltip>
          </div>
        ) : (
          <Tooltip placement="topLeft" title={text?.toString() ?? ""}>
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text?.toString() ?? "-"} />
          </Tooltip>
        );
      default:
        return (
          <Tooltip placement="topLeft" title={text?.toString() ?? "-"}>
            <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text === "" ? "-" : text} />
          </Tooltip>
        );
    }
  };

  const getFilteredTableData = ({ filters, page = getCurrentPage(undefined, filters) }: any) => {
    getMembers({
      page: page,
      limit: rowsPerPage,
      sortBy: sortBy,
      sortType: sortType,
      // searchOn: searchOn,
      reminderStartDate: reminderStartFilter,
      reminderEndDate: reminderEndFilter,
      adtFlag: adtState,
      filterBody: filters,
    });
  };

  const inputref = useRef<any>(null);
  const SearchFilter = (item: any) => {
    return {
      ...item,
      filterDropdown: ["action", "Utilization_Measures_curr_period", "Utilization_Measures_curr_year"].includes(item.dataIndex)
        ? null
        : ({ confirm, visible }: any) => (
            <ColFilters
              item={item}
              inputref={inputref}
              noData={noData}
              confirm={confirm}
              visible={visible}
              getTableData={getFilteredTableData}
              filteredInfo={filteredInfo}
              setFilteredInfo={setFilteredInfo}
              resetFilterPage={resetFilterPage}
              filterDropdowns={filterDropdowns}
              columns={tableConfigData}
            />
          ),
      filterIcon:
        item.dataIndex === "action" || item.dataIndex === "Utilization_Measures_curr_period" || item.dataIndex === "Utilization_Measures_curr_year"
          ? null
          : () => <FilterIcon filteredInfo={filteredInfo} item={item} />,
      render: unSortableColumns.includes(item.dataIndex)
        ? item.render
        : item.colorCoded
        ? (text: any, record: any) => highlighterColorCoded(item, `${text ?? ""}`, record, get(columnSearchInfo, item.dataIndex, ""))
        : (text: any, record: any) => highlighterDiv(item, `${text ?? ""}`, record, get(columnSearchInfo, item.dataIndex, "")),
      onFilter: (value: any, record: any) => (record[item.dataIndex] ? record[item.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : ""),
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => {
            if (inputref.current && isFunction(inputref.current?.select)) {
              inputref.current.select();
            }
          }, 100);
        }
      },
    };
  };

  const highlighterColorCoded = (item: any, text: any, record: any, searchWord: any) => {
    return (
      <span style={{ color: `${getProgressColorCode(record[item.dataIndex + "_flag"])}` }}>
        <Tooltip placement="topLeft" title={text}>
          <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text === "" ? "-" : text} />
        </Tooltip>
      </span>
    );
  };

  const _xyz: Array<{}> = map(cloneDeep(tableConfigData), (dataInfo: any) => {
    dataInfo.title =  unSortableColumns.includes(dataInfo.dataIndex) ?  dataInfo.tableHeaderName : <SortHeader item={dataInfo} pageName={pageName} handleData={handleChange}/>;
    if (expandableColumns.includes(dataInfo.dataIndex)) {
      SearchFilter(dataInfo);
      if (dataInfo.expanded) {
        dataInfo.children = dataInfo.subColumns.map(SearchFilter);
      }
    } else if (["Utilization_Measures_curr_period", "Utilization_Measures_curr_year"].includes(dataInfo.dataIndex)) {
      dataInfo.children = dataInfo?.subColumns?.map(SearchFilter);
    }
    return SearchFilter(dataInfo);
  });

  const keyLoader = (status: boolean) => {
    setInsightLoader(status);
  };

  const isAdtOptionDisabled = (optionLabel: string) => {
    if (optionLabel === "Inpatients" && masterDataForAdmitAndDischarge?.totalHospitalInpatient?.count === 0) {
      return true;
    } else if (optionLabel === "Admissions" && masterDataForAdmitAndDischarge?.totalHospitalAdmission?.totalPatientHospitalAdmissionCount === 0) {
      return true;
    } else if (optionLabel === "Discharges" && masterDataForAdmitAndDischarge?.membersDischarged?.totalPatientHospitalDischargeCount === 0) {
      return true;
    } else if (optionLabel === "ER" && masterDataForAdmitAndDischarge?.totalErCount?.totalPatientHospitalErAdmissionCount === 0) {
      return true;
    }
    return false;
  };

  const tableFilterModalOk = () => {
    if (isFilterModal.onOk) {
      isFilterModal.onOk();
    } else if (isFilterModal.action === "adtClick") {
      formatColumnData(isFilterModal.data?.colData);
      handleAdtData(isFilterModal.data?.adtFlag || "", isFilterModal.data?.value);
    }
    setIsFilterModal({ open: false, action: "", data: {}, colNames: [] });
  };

  const tableFilterModalCancel = () => {
    setIsFilterModal({ open: false, action: "", data: {}, colNames: [] });
  };

  const handleChronicPopover = ({ closeDropdown = true }) => {
    setChronicPopoverOpen(!closeDropdown);
  };

  const ChronicConditionFilter = () => {
    const item = columnListdata.find((col: any) => col.dataIndex === "chronic_name") || {};
    return (
      <ColFilters
        item={item}
        noData={noData}
        getTableData={getFilteredTableData}
        filteredInfo={filteredInfo}
        setFilteredInfo={setFilteredInfo}
        confirm={handleChronicPopover}
        resetFilterPage={resetFilterPage}
        filterDropdowns={filterDropdowns}
        columns={tableConfigData}
      />
    );
  };

  const updateStatus = (patient: any, caseStatusReasonTxt: string = "--", actionModule: string, actionTab: string, actionCard: string, actionLocation: string, actionDescription: string) => {
    if (Array.isArray(patient)) {
      const promises: any[] = [];
      patient.forEach((p: any) => {
        promises.push(membersService.updateStatus(p.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription));
      });
      const result = forkJoin(...promises);
      result.subscribe({
        next: (response: any) => {
          let temp_memberListData = cloneDeep(data);
          const patientStatus: any = {};
          response.forEach((res: any, index: number) => {
            patientStatus[`${patient[index].patient_id}caseStatus`] = res.data?.data?.caseStatus;
            patientStatus[`${patient[index].patient_id}caseLastActionDate`] = res.data?.data?.caseLastActionDate;
          });
          temp_memberListData = temp_memberListData.map((item: any) => {
            let temp = cloneDeep(item);
            if (Object.keys(patientStatus).includes(`${item?.patient_id}caseStatus`)) {
              temp = {
                ...temp,
                patient_case_status: patientStatus[`${item?.patient_id}caseStatus`],
                case_last_action_date: patientStatus[`${item?.patient_id}caseLastActionDate`],
              };
            }
            return temp;
          });
          getMasterData();
          setMemberListData(temp_memberListData);
        },
        error: (error) => {
          console.log(error);
        },
      });
    } else {
      membersService.updateStatus(patient.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
        next: (res) => {
          let temp_memberListData = cloneDeep(data);
          temp_memberListData = temp_memberListData.map((item: any) => {
            let temp = cloneDeep(item);
            if (patient?.patient_id === item?.patient_id) {
              temp = {
                ...temp,
                patient_case_status: res.data?.data?.caseStatus,
                case_last_action_date: res.data?.data?.caseLastActionDate,
              };
            }
            return temp;
          });
          getMasterData();
          setMemberListData(temp_memberListData);
        },
      });
    }
  };
  return (
    <>
      {toggleSelectAllRow ? <div className="backdropOnRowSelection" /> : null}
      <FilterColumnRemoval
        isModalOpen={isFilterModal.open}
        handleOk={() => tableFilterModalOk()}
        action={isFilterModal.action}
        handleCancel={() => tableFilterModalCancel()}
        colNames={isFilterModal.colNames}
      />
      <Layout
        notifications={notificationsData?.notifications}
        newNotificationFlag={newNotificationFlag}
        setNewNotificationFlag={setNewNotificationFlag}
        members={data}
        updateMembers={setMemberListData}
        updateStatus={updateStatus}
      >
        <div className="membersListing">
          <KeyInsight
            masterData={masterData}
            masterDataForAdmitAndDischarge={masterDataForAdmitAndDischarge}
            setMasterDataForAdmitAndDischarge={setMasterDataForAdmitAndDischarge}
            handleAdtClicks={handleAdtClicks}
            keyLoader={keyLoader}
            defaultAdmitAndDischargeStartDate={defaultAdmitAndDischargeStartDate}
            defaultAdmitAndDischargeEndDate={defaultAdmitAndDischargeEndDate}
            isloadingCaseStatus={isloadingCaseStatus}
            memberTypeChangeHandler={memberTypeChangeHandler}
            caseLoadCount={caseLoadCount}
            serviceCallForCaseLoadMasterData={serviceCallForCaseLoadMasterData}
            pageDataLoadingStatus={dataRefreshStatus}
          />

          {comingFromPopulationInst || comingFromMeasures || comingFromChronicBreakdown ? (
            <div
              className="membersListing__compare-title"
              onClick={() => {
                handleComingFromMembersFlag(true);
                navigate(get(routeMap, "dashboard"));
                isCentric && useMemberListStore.getState().clearStore();
              }}
            >
              <div className="membersListing__compare-title__back">
                <Icon name="back" />
              </div>
              <div className="membersListing__compare-title__title">
                {comingFromPopulationInst ? "Populations Insights / " : comingFromChronicBreakdown ? "Chronic Breakdown / " : "Measures / "}
                {comingFromPopulationInst
                  ? get(_report, "reportTitle", "")
                  : comingFromChronicBreakdown
                  ? `${chronicName || ""}`
                  : ` ${measureName || ""}${measureName && selectedYearInMeasuresReport ? " - " : ""}${selectedYearInMeasuresReport || ""}`}{" "}
                /
              </div>
              <div className="membersListing__compare-title__compare">Member List</div>
            </div>
          ) : null}
          <Row className={`tableWrapper ${filtersLoader || keyInsightLoader || loading || columnLoader ? "dnone" : ""}`}>
            <Col span={24}>
              <div className="filters-row">
                <div className="filters-row__select">
                  {registryDropDownAndTableConfig && registryDropDownAndTableConfig?.length > 0 ? (
                    <Select
                      style={{ width: 248 }}
                      placeholder="Select a person"
                      optionFilterProp="children"
                      onChange={(value, option: any) => {
                        memberTypeChangeHandler(value);
                      }}
                      onSearch={() => {}}
                      value={selectedMemberType}
                      defaultValue={
                        comingFromPopulationInst || comingFromMeasures || comingFromChronicBreakdown
                          ? find(registryDropDownAndTableConfig, { label: "Filtered Members" })?.label
                          : registryDropDownAndTableConfig?.filter((item: any) => {
                              if (selectedMemberType) {
                                return selectedMemberType === item?.label;
                              } else {
                                return item?.defaultProperties?.defaultSelection;
                              }
                            })?.[0]?.label
                      }
                      suffixIcon={
                        <span>
                          <img src={IconChevron} alt="chevron-icon" />
                        </span>
                      }
                    >
                      {comingFromPopulationInst || comingFromMeasures || comingFromChronicBreakdown
                        ? [find(registryDropDownAndTableConfig, { label: "Filtered Members" }), ...registryDropDownAndTableConfig?.filter((item: any) => item?.label !== "Filtered Members")]?.map(
                            (member: any) => {
                              return (
                                <Option
                                  key={member?.label}
                                  value={member?.label}
                                  className={`filters-row-option memberList-select-option ${selectedMemberType === member?.label ? "memberList-select-option__selected" : ""}`}
                                  disabled={isAdtOptionDisabled(member?.label)}
                                >
                                  {member?.label}
                                </Option>
                              );
                            }
                          )
                        : cloneDeep(registryDropDownAndTableConfig)
                            ?.filter((item: any) => item?.label !== "Filtered Members")
                            ?.map((member: any) => {
                              return (
                                <Option
                                  key={member?.label}
                                  value={member?.label}
                                  className={`filters-row-option memberList-select-option ${selectedMemberType === member?.label ? "memberList-select-option__selected" : ""}`}
                                  disabled={isAdtOptionDisabled(member?.label)}
                                >
                                  {member?.label}
                                </Option>
                              );
                            })}
                      {/* {comingFromPopulationInst
                          ? cloneDeep(memberListRegistryDropDown)
                              ?.sort((a:any, b:any) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1))
                              ?.map((member:any) => {
                                return (
                                  <Option key={member.key} value={member.value} className="filters-row-option memberlist-select-option" disabled={isAdtOptionDisabled(member.label)}>
                                    {member.label}
                                  </Option>
                                );
                              })
                          : memberListRegistryDropDown && memberListRegistryDropDown?.length > 0 ?
                          memberListRegistryDropDown?.slice(1)
                              .sort((a:any, b:any) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1))
                              ?.map((member:any) => {
                                return (
                                  <Option key={member.key} value={member.value} className="filters-row-option memberlist-select-option" disabled={isAdtOptionDisabled(member.label)}>
                                    {member.label}
                                  </Option>
                                );
                              }) : null} */}
                    </Select>
                  ) : null}
                  <FilterCountBtn filteredInfo={filteredInfo} showFilters={showFilters} setShowFilters={setShowFilters} />
                </div>
                {!isCentric && <div className="reminderDatePicker">
                  <div className="reminderDateLabel">
                    <span className="icon-calendar f18" />
                    <span>Reminder Date:</span>
                  </div>
                  <RangePicker
                    separator={<span className="range-picker-separator-members-list">-</span>}
                    picker="date"
                    size="middle"
                    format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
                    allowClear={true}
                    value={reminderStartFilter && reminderEndFilter ? [moment(moment(reminderStartFilter).format("MM-DD-YYYY")), moment(moment(reminderEndFilter).format("MM-DD-YYYY"))] : undefined}
                    dropdownClassName="reminder-picker"
                    ranges={{
                      "More Filters:": [null, null],
                      "Next 7 Days": [moment(), moment().add(7, "days")],
                      "Last 7 Days": [moment().subtract(7, "days"), moment()],
                    }}
                    onChange={(range: any) => {
                      handleReminderDateFilter(range);
                    }}
                  />
                </div>}
                <Tooltip title="Download XLS" overlayClassName="download-tooltip">
                  <i className="btn-icon icon-download clr-n125" style={{ width: "20px", height: "42px" }} onClick={toggleDownloadDrawer} />
                </Tooltip>
                <Spin spinning={filtersLoader} size={"small"} indicator={<HealthemSpinner size={18} />}>
                  <Popover
                    content={<ChronicConditionFilter />}
                    title="CHRONIC CONDITIONS"
                    trigger="click"
                    visible={chronicPopoverOpen}
                    overlayClassName={`column_filter__popover`}
                    onVisibleChange={(e) => setChronicPopoverOpen(e)}
                  >
                    <Badge dot={filteredInfo["chronic_name"]} offset={[-12, 12]}>
                      <span
                        className={`clr-n125`}
                        style={{ display: "block", marginLeft: "8px", height: "42px", border: "1px solid #dfe1e6", borderRadius: "4px", cursor: "pointer", padding: "10px 10px" }}
                        onClick={() => setChronicPopoverOpen((chronicPopoverOpen) => !chronicPopoverOpen)}
                      >
                        <Icon name="chronic" />
                      </span>
                    </Badge>
                  </Popover>
                </Spin>
                {<i className="btn-icon icon-settings clr-n125" style={{ height: "42px" }} onClick={() => toggleDrawerViewType(layerViews[1])} />}
              </div>
              <ClearFilters filteredInfo={filteredInfo} setFilteredInfo={setFilteredInfo} getFilteredTableData={getFilteredTableData} showFilters={showFilters} resetFilterPage={resetFilterPage} />
              <div className={`${!noData ? "dnone" : ""}`}>
                <Table
                  id="member-table"
                  bordered={true}
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ x: true }}
                  onChange={handleChange}
                  columns={_xyz
                    ?.sort((a: any, b: any) => a.sequence - b.sequence)
                    .filter((item: any) => {
                      return item.isSelected;
                    })}
                  dataSource={[]}
                  locale={TableLocale}
                />
              </div>
              <div className={`${noData ? "dnone" : ""}`}>
                <div>
                  {viewType === "tableView" ? (
                    <>
                      {caseStatusExpanded && (
                        <div className="case-status-expanded-container">
                          <div
                            className="case-status-expanded"
                            style={{
                              transform: `translateX(${caseStatusPosition.left}px)`,
                              width: `${caseStatusPosition.width}px`,
                            }}
                          >
                            <span>CASE STATUS</span>
                            <i
                              className="btn-icon icon-chevron-solid-v-col"
                              style={{
                                transform: "rotate(90deg)",
                                marginLeft: 0,
                                padding: 10,
                              }}
                              onClick={() => collapseCaseStatus()}
                            ></i>
                          </div>
                        </div>
                      )}
                      <Table
                        id="member-table"
                        bordered={true}
                        tableLayout="fixed"
                        pagination={false}
                        onChange={handleChange}
                        scroll={{ y: isCentric ? (window.innerHeight - 150) : (window.innerHeight - 370) }}
                        rowSelection={
                          data.length === 0
                            ? undefined
                            : {
                                type: selectionType,
                                ...rowSelection,
                              }
                        }
                        columns={_xyz
                          ?.sort((a: any, b: any) => a.sequence - b.sequence)
                          .filter((item: any) => {
                            return item.isSelected;
                          })}
                        dataSource={data}
                        locale={TableLocale}
                      />
                    </>
                  ) : (
                    <TableCard empData={data} handleClick={(record: any) => goToMemberDetails(record)} />
                  )}
                  <div className={`pagination ${isEmpty(data) ? "dnone" : ""}`}>
                    <div>
                      Show
                      <Select
                        defaultValue={rowsPerPage}
                        style={{ width: 64, margin: "8px 8px" }}
                        onClick={() => setRowsPerPageToggle(!rowsPerPageToggle)}
                        onChange={(e) => setPageSize(e)}
                        open={rowsPerPageToggle}
                        suffixIcon={<CaretDownOutlined />}
                      >
                        {rowsPerPageArray.map((val) => (
                          <Option key={val} value={val} className="memberlist-select-option">
                            {val}
                          </Option>
                        ))}
                      </Select>
                      rows per page
                    </div>
                    {data?.length > 0 && (
                      <Pagination
                        total={totalPages}
                        showTotal={(total: any, range: any) => (
                          <>
                            <span style={{ color: "#091e42" }}>
                              {rowsPerPage * getCurrentPage() - (rowsPerPage - 1)} -{" "}
                              {rowsPerPage * getCurrentPage() > data?.length
                                ? data?.length + rowsPerPage * getCurrentPage() - (rowsPerPage - 1) - 1
                                : rowsPerPage * getCurrentPage() + rowsPerPage * getCurrentPage() - (rowsPerPage - 1) - 1}
                              {/* {range[0]} - {range[1]} */}
                              {/* {range.map(r=>console.log(r))} */}
                            </span>
                            <span> of {totalPages}</span>
                          </>
                        )}
                        key={rowsPerPage}
                        defaultPageSize={rowsPerPage}
                        defaultCurrent={getCurrentPage()}
                        current={getCurrentPage()}
                        showSizeChanger={false}
                        onChange={(e: any) => {
                          changeCurrentPage(e);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className={`tableWrapper ${!(filtersLoader || keyInsightLoader || loading || columnLoader) ? "dnone" : ""}`}>
            <Col span={24}>
              <div className="filters-row">
                <div className="filters-row__select filters-row__select_preloader">
                  <div className="member_select_preloader shimmerBG"></div>
                </div>
                <div className="reminderDatePicker reminderDatePicker_preloader shimmerBG"></div>
                <div className="btn-icon_preloader shimmerBG"></div>
                <div className="btn-icon_preloader shimmerBG"></div>
                <div className="btn-icon_preloader shimmerBG"></div>
                <div className="btn-icon_preloader shimmerBG" style={{ marginRight: 0 }}></div>
                <div className="btn-icon_preloader shimmerBG" style={{ marginLeft: 0 }}></div>
              </div>
              <TablePreloader rows={rowsPerPage} />
              <div className="pagination">
                <div className="rowperpage_preloader shimmerBG"></div>
                <div className="pagination_text_preloader shimmerBG"></div>
              </div>
            </Col>
          </Row>
        </div>
        <RefreshData />
        <Drawer
          destroyOnClose={true}
          title={drawerViewtype.label}
          placement="right"
          closable={false}
          onClose={onCloseConfigDrawer}
          visible={isOpenDrawer}
          key="right"
          width={drawerViewtype.width}
          bodyStyle={{ padding: drawerViewtype.padding }}
          className={`membersListingDrawer ${drawerViewtype.className}`}
          footer={drawerFooter()}
          extra={drawerHeader()}
        >
          <Spin spinning={isDrawerLoading} indicator={<HealthemSpinner size={60} />}>
            {drawerView()}
          </Spin>
        </Drawer>
        {/* download drawer */}
        <Drawer
          destroyOnClose={true}
          placement="bottom"
          closable={false}
          onClose={toggleDownloadDrawer}
          visible={isDownloadDrawerOpen}
          key="bottom"
          height="125px"
          maskClosable={true}
          className={"downloadDrawer"}
          mask={false}
        >
          <DownloadSection
            columns={tableConfigData}
            downloadColData={downloadColData}
            downloadType={downloadType}
            sortData={getSortandADTMap(adtState, sortBy, sortType)}
            reminderStartFilter={reminderStartFilter}
            reminderEndFilter={reminderEndFilter}
            totalUserCount={totalCount}
            adtState={getSortandADTMap(adtState, sortBy, sortType).adtMapping}
            filters={filteredInfo}
            totalUser={data?.filter((d) => selectedRows?.selectedRowKeys?.includes(d?.patient_id))}
            patientListForFilter={
              adtState === "Filtered Members" && comingFromMeasures
                ? measuresPatientIds
                : adtState === "Filtered Members" && comingFromChronicBreakdown
                ? chronicPatientIds
                : adtState === "isFiltered"
                ? pIds
                : ADTPatientIdList?.[adtState] && ADTPatientIdList?.[adtState]?.length > 0
                ? ADTPatientIdList?.[adtState]
                : []
            }
            onClose={toggleDownloadDrawer}
          />
        </Drawer>
      </Layout>
    </>
  );
};



export default withAITracking(reactPlugin, Members);
